"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";

import CoachStrip from "@/components/nav/CoachStrip";
import MobileNavMenu from "@/components/nav/MobileNavMenu";
import { MobileHeader, SignedOutBar } from "@/components/nav/NavHeaders";
import NavCoachPane from "@/components/nav/NavCoachPane";
import {
  BookmarkStar,
  bookmarksDrawerSection,
  bookmarksPanelView,
} from "@/components/nav/NavBookmarks";
import NavPanel from "@/components/nav/NavPanel";
import NavRail from "@/components/nav/NavRail";
import NavSearchDialog from "@/components/nav/NavSearchDialog";
import NavSearchPane from "@/components/nav/NavSearchPane";
import NavTopBar from "@/components/nav/NavTopBar";
import SearchShortcutsTip from "@/components/nav/SearchShortcutsTip";
import {
  SideNavSlotsProvider,
  useSideNavSlots,
} from "@/components/nav/SideNavSlots";
import { useStudioBranding } from "@/components/nav/useStudioBranding";
import { useStudioNavData } from "@/components/nav/useStudioNavData";
import { useWorkspaceSwitcher } from "@/components/nav/useWorkspaceSwitcher";
import { useCurrentHash } from "@/hooks/useCurrentHash";
import { NAV_BELOW_TOP_BAR } from "@/components/nav/nav-styles";
import { useHelpSheet } from "@/components/nav/useHelpSheet";
import { BOOKMARKS_KEY, useNavBookmarks } from "@/components/nav/useNavBookmarks";
import { usePanelPreference } from "@/components/nav/useNavFrameState";
import { useActiveNavKey } from "@/components/nav/useActiveNavKey";
import { useNavCoach } from "@/components/nav/useNavCoach";
import { useNavColumnMotion } from "@/components/nav/useNavColumnMotion";
import { useNavShortcuts } from "@/components/nav/useNavShortcuts";
import { useChatsMenu } from "@/components/nav/useChatsMenu";
import { useNavSearch } from "@/components/nav/useNavSearch";
import { useNavTypeToSearch } from "@/components/nav/useNavTypeToSearch";
import { useApplePlatform } from "@/hooks/useApplePlatform";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  buildNavSections,
  collectNavShortcuts,
  createNavContext,
  hasVisibleNavItems,
} from "@/lib/navigation/nav-items";
import { stripNavigationLocalePrefix } from "@/lib/navigation/is-nav-item-active";
import { TRAINING_MODE_HOME } from "@/lib/navigation/nav-manifest";
import { isSimulatorRoute } from "@/lib/simulatorRoutes";
import { isTrainingModeRestrictedUser } from "@/lib/training-mode-users";
import { cn } from "@/lib/utils";

const OUTLINE_KEY = "outline";
// Where the icon rail shows (Tailwind's `md`). Search opens as a pane beside
// it; below this there is no rail, so search opens as a dialog instead.
const RAIL_SCREEN = "(min-width: 768px)";
// Wide enough for the menu panel and Sabine's chat to stay open together
// with the page still readable between them; narrower, her chat takes the
// panel's room while it is open.
const ROOM_FOR_BOTH = "(min-width: 1280px)";
// A cream column (the panel-and-search box beside the rail, Sabine's pane at
// the right edge) owns the corner where it meets the navy and the sheet, and
// runs under the sheet's edge so that corner is geometry (`.nav-column` and
// `.nav-column-end` in app/globals.css); the box each sits in draws the navy
// notch for when it is closed. Every column is named here
// (sideNavFrame.test.js).
const CREAM_CORNER = "nav-column";
const CREAM_CORNER_END = "nav-column-end";
const NAVY_NOTCH_END = "nav-columns-end";

const menuView = (menu, child, rowAction) =>
  menu && {
    key: menu.key,
    title: menu.menuLabel,
    subtitle: menu.subtitle,
    items: menu.items,
    crossLink: menu.crossLink,
    child,
    rowAction,
  };

/**
 * The app's navigation: a navy frame (a top bar and an icon rail with one
 * button per menu) around a cream panel listing the selected menu's links,
 * and the page content on a white sheet to their right. A training-mode user
 * gets the same frame with fewer menus, which `buildNavSections` hands back
 * for them, and Home opens their training (`TRAINING_MODE_HOME`). The bar (NavTopBar) holds the
 * studio's mark, search, Ask Sabine and Help; the account menu lives on the
 * rail. Below `md` the frame becomes a cream header with a drawer.
 *
 * What the menus contain is declared once in `lib/navigation/nav-manifest.js`
 * and resolved for this viewer by `lib/navigation/nav-items.js`; this file
 * only gathers the session, studio and module state those need and arranges
 * the pieces. Adding a nav link belongs in the manifest, not here.
 *
 * The panel shows the menu the route belongs to, or the page's own outline
 * when it registered one (SideNavSlots). A rail button, or the menu's name
 * in the outline's header, shows that menu instead; pressing the rail button
 * again hides the panel. The choice lasts until the route changes.
 *
 * A page that registered a coach (its Sabine chat) gets the Ask button at
 * the right of the bar; the chat opens in a column at the right edge, with
 * its header in the bar above it, so the menu panel stays open beside it
 * (on a screen too narrow for both, the chat takes the panel's room while
 * it is open, and search or a menu closes it). Search and the menu panel
 * share the column beside the rail. Signed-out pages that register one (the
 * shared playbook) get a cream strip with the Ask button and the pane
 * without the rail. Anyone signed in also gets Help in the bar (and in the
 * phone header); its sheet floats over the page's right edge, beside the
 * chat when that is open.
 */
function SideNavFrame({ children }) {
  const { data: session, update } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const normalizedPathname = stripNavigationLocalePrefix(pathname || "");
  const isSimulator = isSimulatorRoute(normalizedPathname);

  const [currentHash, setCurrentHash] = useCurrentHash(pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelOpen, setPanelOpen] = usePanelPreference();
  // In a live simulation the panel starts hidden and a peek is not remembered.
  const [peekOpen, setPeekOpen] = useState(false);
  const [pinnedKey, setPinnedKey] = useState(null);
  const searchButtonRef = useRef(null);
  const search = useNavSearch({ triggerRef: searchButtonRef });
  const { setOpen: setSearchOpen } = search;
  // The bar's elements the search pane renders its input into (NavSearchRow)
  // and the chat its header into (NavTopBar).
  const [searchInputSlot, setSearchInputSlot] = useState(null);
  const [chatHeaderSlot, setChatHeaderSlot] = useState(null);
  const hasRail = useMediaQuery(RAIL_SCREEN);
  const roomForBoth = useMediaQuery(ROOM_FOR_BOTH);
  const apple = useApplePlatform();
  const { topAction, outline, coach: coachSlot } = useSideNavSlots();
  const helpSheet = useHelpSheet(Boolean(session));

  useEffect(() => {
    setMenuOpen(false);
    setPeekOpen(false);
    setPinnedKey(null);
    setSearchOpen(false);
  }, [pathname, setSearchOpen]);

  const { displayName, displayLogo } = useStudioBranding({
    session,
    searchParams,
    normalizedPathname,
  });
  const { enabledModules, isCommunityLeader, modules } =
    useStudioNavData(session);
  const switcherState = useWorkspaceSwitcher({ session, update });

  const user = session?.user;
  const workspace = session?.workspace;
  const canSwitchStudios = Boolean(user?.superUser || user?.role === "admin");
  // "/" would only bounce a training-mode rep to their training, so their
  // Home goes straight there.
  const trainingMode = isTrainingModeRestrictedUser(user);
  const homeHref = trainingMode ? TRAINING_MODE_HOME : "/";
  // Home's chat opens with the page (HomeCoach `defaultOpen`). The frame
  // knows that from the route, so the server draws the column open before
  // the page's chat has registered, and the first paint already has it.
  // Without a studio, Home is an access notice with no chat.
  const pageOpensChat =
    Boolean(session?.workspace) && !trainingMode && normalizedPathname === "/";
  const coach = useNavCoach({ coach: coachSlot, hasRail, pathname, pageOpensChat });

  const menus = useChatsMenu(
    buildNavSections(
      createNavContext({
        user,
        workspace,
        enabledModules,
        modules,
        isCommunityLeader,
      }),
    ).filter((menu) => hasVisibleNavItems(menu.items)),
    {
      visible:
        menuOpen || pinnedKey === "chats" || normalizedPathname.startsWith("/chats"),
    },
  );

  // A training-mode rep's rail is deliberately short, so no bookmarks.
  const query = searchParams?.toString();
  const bookmarks = useNavBookmarks({
    workspaceId: trainingMode ? null : user?.workspaceId,
    userId: user?.id,
    menus,
    currentHref: `${pathname || ""}${query ? `?${query}` : ""}${currentHash ? `#${currentHash}` : ""}`,
  });
  const starLink = bookmarks.enabled
    ? (link) => !link.recent && <BookmarkStar link={link} bookmarks={bookmarks} />
    : undefined;

  // Option/Alt + a letter goes straight to that page. The tour that teaches it
  // points at the first menu that has any, and quotes that menu's letters.
  const shortcuts = session ? collectNavShortcuts(menus) : [];
  useNavShortcuts(shortcuts);
  const tipMenuRef = useRef(null);
  const tipMenuKey = shortcuts[0]?.menuKey;

  // Typing at the nav opens search with the shown menu's pages first.
  const activeMenuKey = useActiveNavKey(menus, normalizedPathname, pinnedKey);
  const shownMenu =
    menus.find((menu) => menu.key === pinnedKey) ||
    menus.find((menu) => menu.key === activeMenuKey);
  const typeToSearchProps = useNavTypeToSearch({
    pagesFirst: true,
    menuLabel: shownMenu?.menuLabel || "",
  });

  // Where there is no room for both, opening search (the row, ⌘K, typing at
  // the nav) closes the chat, in the same frame: it is shown only while
  // search is closed. Until the screen is measured there is room: the panel
  // and Home's chat are both drawn open on the server.
  const noRoomForBoth = roomForBoth === false;
  useEffect(() => {
    if (search.open && noRoomForBoth) coach.setOpen(false);
  }, [search.open, noRoomForBoth, coach.setOpen]);
  const coachShown = coach.open && (!noRoomForBoth || !search.open);
  const chatTakesPanel = coachShown && noRoomForBoth;

  // The outline stands in for the route's menu, so its header names that
  // menu as the way back; a rail button leads the same way. While the page
  // is open that menu names the outline as the way forward, so the trail
  // reads "Learn › Playbook" from either side.
  const activeMenu = menus.find((menu) => menu.key === activeMenuKey);
  const outlineView = outline && {
    key: OUTLINE_KEY,
    title: outline.title,
    items: outline.links,
    activeHrefs: outline.activeHrefs || [],
    parent: activeMenu && { key: activeMenu.key, label: activeMenu.menuLabel },
  };
  const pinnedMenu = menus.find((menu) => menu.key === pinnedKey);
  const view =
    (pinnedKey === BOOKMARKS_KEY && bookmarks.enabled && bookmarksPanelView(bookmarks)) ||
    (pinnedKey !== OUTLINE_KEY &&
      menuView(
        pinnedMenu,
        outline && pinnedMenu === activeMenu
          ? { key: OUTLINE_KEY, label: outline.title }
          : undefined,
        starLink,
      )) ||
    outlineView ||
    menuView(activeMenu, undefined, starLink);

  const panelVisible = Boolean(view) && (isSimulator ? peekOpen : panelOpen);
  // The search pane takes the panel's place while open, and so does the
  // chat where there is no room for both; the panel's own state is
  // untouched, so closing either brings the panel back as it was.
  const panelShown = panelVisible && !search.open && !chatTakesPanel;
  const columnMotion = useNavColumnMotion(
    `${(search.open && hasRail && "search") || (panelShown && "panel") || ""}|${coachShown ? "chat" : ""}`,
  );

  // Open before the page's chat has registered (Home on the server), the
  // pane is the empty column the chat then fills in place.
  const coachPane = (coach.registered || coach.open) && (
    <NavCoachPane
      open={coachShown}
      instant={coach.instant}
      fullscreen={coach.fullscreen}
      onClose={() => coach.setOpen(false)}
      onFullscreenChange={hasRail ? coach.setFullscreen : null}
      headerSlot={hasRail ? chatHeaderSlot : null}
      // Relative, not sticky like a signed-out pane: the box around it sticks,
      // and a sticky pane would paint its cream notch under that box's navy.
      className={session ? cn("md:relative md:h-full", CREAM_CORNER_END) : undefined}
    >
      {coachSlot?.chat}
    </NavCoachPane>
  );
  // Every Ask button closes search and the drawer first: search closes the
  // chat when it opens, so none of them ever show together.
  const openCoach = coach.registered
    ? () => {
        search.close();
        setMenuOpen(false);
        coach.setOpen(true);
      }
    : null;
  // A fullscreen chat is modal: everything else in the frame goes inert, so
  // Tab and a screen reader stay inside it.
  const behindCoach = coach.fullscreen || undefined;
  if (!session) {
    return (
      <>
        <SignedOutBar
          displayName={displayName}
          displayLogo={displayLogo}
          onOpenCoach={openCoach}
          inert={behindCoach}
        />
        <div className="flex min-h-0 grow flex-col md:flex-row">
          <div inert={behindCoach} className="flex min-h-0 min-w-0 grow flex-col">
            {coach.registered && (
              <CoachStrip
                placeholder={coachSlot.placeholder}
                open={coach.open}
                onOpen={openCoach}
              />
            )}
            {children}
          </div>
          {coachPane}
        </div>
      </>
    );
  }

  const showPanel = (next) =>
    isSimulator ? setPeekOpen(next) : setPanelOpen(next);
  const selectMenu = (key) => {
    search.close();
    if (noRoomForBoth) coach.setOpen(false);
    if (panelVisible && view?.key === key) {
      setPinnedKey(null);
      showPanel(false);
      return;
    }
    setPinnedKey(key);
    showPanel(true);
  };
  const togglePanel = () => {
    if (search.open) {
      search.close();
      return;
    }
    if (chatTakesPanel) {
      coach.setOpen(false);
      return;
    }
    if (panelVisible) setPinnedKey(null);
    showPanel(!panelVisible);
  };

  const studioName = workspace?.name || displayName;
  const logoSrc = workspace?.image || displayLogo;
  const drawerSections = [
    bookmarks.enabled && bookmarksDrawerSection(bookmarks),
    outline && {
      label: outline.title,
      subtitle: "On this page",
      items: outline.links,
      activeHrefs: outline.activeHrefs || [],
    },
    ...menus.map((menu) => ({
      label: menu.menuLabel,
      subtitle: menu.subtitle,
      items: menu.items,
    })),
  ].filter(Boolean);

  return (
    <div
      data-nav-motion={columnMotion}
      className="flex min-h-0 grow flex-col bg-surface-chrome md:bg-surface-chrome-ink"
    >
      <MobileHeader
        logoSrc={logoSrc}
        displayName={studioName}
        user={user}
        menuOpen={menuOpen}
        onOpenMenu={() => setMenuOpen(true)}
        onOpenCoach={openCoach}
        help={helpSheet.help}
        inert={behindCoach}
      />
      <MobileNavMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        pathname={pathname}
        currentHash={currentHash}
        onNavigateHash={setCurrentHash}
        sections={drawerSections}
        homeHref={homeHref}
      />
      <NavTopBar
        inert={behindCoach}
        studio={{
          displayName: studioName,
          displayLogo: logoSrc,
          switcher:
            canSwitchStudios && switcherState.availableWorkspaces.length > 0
              ? {
                  currentWorkspace: switcherState.switcherCurrentWorkspace,
                  workspaces: switcherState.availableWorkspaces,
                  value: switcherState.selectedWorkspaceId,
                  isSwitching: switcherState.isSwitchingWorkspace,
                  onSelect: switcherState.handleWorkspaceSwitch,
                }
              : null,
        }}
        search={{
          open: search.open,
          buttonRef: searchButtonRef,
          inputSlotRef: setSearchInputSlot,
          onOpen: () => setSearchOpen(true),
        }}
        ask={
          openCoach && {
            placeholder: coachSlot.placeholder,
            onOpen: openCoach,
          }
        }
        help={helpSheet.help}
        chatOpen={coachShown}
        chatInstant={coach.instant}
        chatHeaderSlotRef={setChatHeaderSlot}
      />

      <div className="flex min-h-0 grow flex-col md:flex-row">
        <nav
          {...typeToSearchProps}
          inert={behindCoach}
          data-app-nav
          aria-label="Main navigation"
          className={cn(
            "nav-columns hidden md:flex md:shrink-0 print:hidden",
            NAV_BELOW_TOP_BAR,
          )}
        >
          <NavRail
            menus={menus}
            currentKey={activeMenuKey}
            openKey={panelShown ? view.key : null}
            onSelectMenu={selectMenu}
            isHome={normalizedPathname === homeHref}
            homeHref={homeHref}
            bookmarks={bookmarks.enabled}
            user={user}
            panelOpen={panelShown}
            panelAvailable={Boolean(view) || search.open || chatTakesPanel}
            onTogglePanel={togglePanel}
            menuButtonRef={tipMenuRef}
            tipMenuKey={tipMenuKey}
          />
          <div className={cn("relative flex min-h-0 bg-surface-chrome", CREAM_CORNER)}>
            {view && (
              <NavPanel
                open={panelShown}
                view={view}
                topAction={view.key === activeMenuKey ? topAction : null}
                pathname={pathname}
                currentHash={currentHash}
                onNavigateHash={setCurrentHash}
                onSelectMenu={selectMenu}
              />
            )}
            <NavSearchPane
              search={search}
              open={search.open && hasRail}
              apple={apple}
              inputSlot={searchInputSlot}
            />
          </div>
        </nav>
        {helpSheet.sheet}
        {/* `hasRail` is undefined until its layout effect runs, so the dialog is briefly
            mounted on a wide screen too — closed, because every way to open
            search is wired by an effect of the same commit. */}
        {!hasRail && <NavSearchDialog search={search} apple={apple} />}

        <SearchShortcutsTip
          searchButtonRef={searchButtonRef}
          menuRef={tipMenuRef}
          menuShortcuts={shortcuts.filter((s) => s.menuKey === tipMenuKey)}
          // Home only: other pages run spotlight tours of their own, and two at
          // once would stack.
          enabled={normalizedPathname === "/"}
        />

        <div inert={behindCoach} className="flex min-h-0 min-w-0 grow flex-col">
          {/* overflow-x-clip, not -hidden: hidden would make the sheet a
              scroll container and strand every `sticky` header inside it. */}
          {/* The sheet starts where the bar ends, on one line with the
              cream beside it, so the bar's controls have the same 10px of
              navy above and below them and nothing moves when a panel opens.
              It sits on navy whatever is open, rounded at both top
              corners: an open column runs under its corner and paints the
              cream there (CREAM_CORNER, CREAM_CORNER_END). */}
          <div className="flex min-h-0 min-w-0 grow flex-col overflow-x-clip bg-white md:rounded-t-3xl print:rounded-none">
            {children}
          </div>
        </div>
        {/* Sticky under the bar, except while the chat fills the screen: a
            sticky box would hold it under the bar's layer. */}
        <div
          className={cn(
            "relative md:flex md:shrink-0 print:hidden",
            NAVY_NOTCH_END,
            !coach.fullscreen && NAV_BELOW_TOP_BAR,
          )}
        >
          {coachPane}
        </div>
      </div>
    </div>
  );
}

export default function SideNav({ children }) {
  return (
    <SideNavSlotsProvider>
      <SideNavFrame>{children}</SideNavFrame>
    </SideNavSlotsProvider>
  );
}
